import React, { useState } from 'react';
import { Heading, IconButton, Tooltip } from '@lumx/react';
import MdiIcon from '@mdi/react';
import {
  mdiAccountMultipleOutline,
  mdiArrowLeft,
  mdiChevronDown,
  mdiChevronUp,
  mdiInformationOutline,
} from '@mdi/js';
import PlatformGroupsPage from './PlatformGroupsPage';
import EmptyState from './shared/EmptyState';
import { useWizard } from '../context/WizardContext';
import { getSiteGroupCategories, getSiteGroupCategoriesBySite, SiteGroupCategory } from '../data/mockData';
import './platform-groups.css';

interface Props {
  nodeId: string;
  nodeName: string;
  hasSitesMapped: boolean;
  kind?: 'site';
  onBack: () => void;
}

const CategoryList: React.FC<{ categories: SiteGroupCategory[] }> = ({ categories }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  return (
    <div className="pg-list" style={{ flex: 1, overflowY: 'auto' }}>
      {categories.map(cat => {
        const isExpanded = expanded.has(cat.id);
        return (
          <div key={cat.id} className="pg-category">
            <div className="pg-category__head">
              <Heading as="h2" typography="subtitle1">{cat.name}</Heading>
              <div style={{ flex: 1 }} />
              <button
                className="pg-icon-btn"
                onClick={() => toggle(cat.id)}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <MdiIcon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={0.85} />
              </button>
            </div>
            {isExpanded && (
              <div className="pg-category__body">
                {cat.groups.map(group => (
                  <div key={group.id} className="pg-group-row">
                    <Heading as="h2" typography="subtitle1">{group.name}</Heading>
                    <div style={{ flex: 1 }} />
                    <Tooltip label={`${group.members.toLocaleString()} members`} placement="top">
                      <button className="pg-icon-btn pg-icon-btn--muted" aria-label="Group info">
                        <MdiIcon path={mdiInformationOutline} size={0.75} />
                      </button>
                    </Tooltip>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const GroupsPage: React.FC<Props> = ({ nodeId, nodeName, hasSitesMapped, kind, onBack }) => {
  const { navigateTo } = useWizard();
  const isSite = kind === 'site';
  const pageTitle = isSite ? `Site groups in ${nodeName}` : `Groups in ${nodeName}`;

  // Global → PlatformGroupsPage
  if (nodeId === '__global__') {
    return (
      <main className="main-content" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <PlatformGroupsPage onBack={onBack} />
      </main>
    );
  }

  // No sites mapped → empty state (only for node kind)
  if (!isSite && !hasSitesMapped) {
    return (
      <main className="main-content" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="users-page">
          <div className="users-page__header">
            <IconButton label="Back" icon={mdiArrowLeft} emphasis="low" onClick={onBack} />
            <Heading as="h1" typography="headline">{pageTitle}</Heading>
          </div>
          <EmptyState
            icon={mdiAccountMultipleOutline}
            title="No groups yet"
            body="Assign a site to this unit first."
            action={{ label: 'Go to Org mapping', onClick: () => { onBack(); navigateTo('site-mapping'); } }}
          />
        </div>
      </main>
    );
  }

  const categories: SiteGroupCategory[] = isSite
    ? getSiteGroupCategoriesBySite(nodeId)
    : getSiteGroupCategories(nodeId);

  return (
    <main className="main-content" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div className="users-page">
        <div className="users-page__header">
          <IconButton label="Back" icon={mdiArrowLeft} emphasis="low" onClick={onBack} />
          <Heading as="h1" typography="headline">{pageTitle}</Heading>
        </div>
        <CategoryList categories={categories} />
      </div>
    </main>
  );
};

export default GroupsPage;
